"use client";

import { IconBulb } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/Button";
import { visibleLegendSections } from "@/features/layers/legendSwatches";
import { useSecondaryLayers } from "@/features/layers/useSecondaryLayers";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { LegendDropdown } from "./LegendDropdown";

/**
 * The legend toggle and its panel.
 *
 * A tenant can ask for the legend to start open (`isMapLegendOpen`), which is why
 * the open state is owned by the toolbar rather than kept locally — the toolbar
 * allows only one dropdown open at a time.
 */
export function LegendMenu({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations();
  const { mapLegend } = useProjectConfigs();
  const { sections, isActive } = useSecondaryLayers();

  const items = mapLegend?.mapLegendItems ?? [];
  // A project can legend itself entirely through its layers, with no authored
  // image at all — so the button appears when either half has something to say.
  const hasContent =
    items.length > 0 || visibleLegendSections(sections, isActive).length > 0;
  const shouldOpenOnLoad = mapLegend?.isMapLegendOpen;

  const openedOnLoad = useRef(false);
  useEffect(() => {
    if (shouldOpenOnLoad && !openedOnLoad.current && !isOpen) {
      openedOnLoad.current = true;
      onToggle();
    }
  }, [shouldOpenOnLoad, isOpen, onToggle]);

  if (!mapLegend?.showMapLegend || !hasContent) return null;

  return (
    <div className="sort relative inline-block items-center justify-self-end text-right text-xs">
      <Button
        variant="ghost"
        size="xs"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <IconBulb size={16} color="#888" aria-hidden="true" />
        <span className="text-sm font-normal">{t("legend")}</span>
      </Button>

      {isOpen && <LegendDropdown items={items} onClose={onToggle} />}
    </div>
  );
}
